import { useState, useEffect } from "react";
import { dropDownGetter } from "../utils";
/**
 * Custom hook for fetching dropdown data from the server
 * @param {string} type - The type of data to fetch (e.g., 'jobTitles', 'financeManager')
 * @param {number} initialLimit - Initial number of items to fetch
 * @param {object} dependencies - Additional dependencies that should trigger a refetch
 * @param {object} additionalParams - Additional parameters to pass to the dropDownGetter function
 * @returns {object} - Object containing data, loading state, search handling, and pagination
 */
const useDropdownData = (
  type,
  initialLimit = 5,
  dependencies = {},
  additionalParams = {},
  instance = null
) => {
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(initialLimit);
  const [starting_afteracc, setStarting_afteracc] = useState(null);
  const [starting_aftertag, setStarting_aftertag] = useState(null);
  // Handle search input change - Modified to handle both direct value and key/value pairs
  const handleSearch = (keyOrValue, value) => {
    // Check if this is a key/value pair (from FormFieldSet) or direct value
   // console.log("handleSearch called with:", keyOrValue, value);
    if (
      typeof keyOrValue === "object" ||
      (typeof value !== "undefined" && keyOrValue === type)
    ) {
      // This is from FormFieldSet with key/value structure
      setSearchValue(value);
   //  console.log("Search value set to:", value);
    } else {
      // This is a direct value
      setSearchValue(keyOrValue);
    }
  };

  // Reset search value
  const resetSearch = () => {
    setSearchValue("");
  };

  // Handle pagination (load more)
  const loadMore = () => {
    setLimit((prevLimit) => prevLimit + 5);
  };

  // Check if container is near bottom to trigger loadMore
  const handleScroll = (containerRef) => {
    const container = containerRef.current;
    if (!container) return;

    const nearBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 10;

    if (nearBottom && !loading) {
      loadMore();
    }
  };
  // Fetch data from server
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await dropDownGetter(type, limit, searchValue, {
          ...additionalParams,
          starting_afteracc,
          starting_aftertag,
        });
       // console.log(`Fetched ${type} data:`, result, searchValue);
        setData(result.formattedResult || []);
        setOriginalData(result.result || []);
        if (type === "getCampaignAccounts") {
          const campaignData = result.result || [];
          setStarting_afteracc(campaignData?.[0]?.next_starting_after);
          setStarting_aftertag(campaignData?.[1]?.next_starting_after);
        }
      } catch (error) {
        console.error(`Error fetching ${type} data:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    type,
    limit,
    searchValue,
    JSON.stringify(dependencies),
    JSON.stringify(additionalParams),
    instance,
  ]);

  return {
    originalData,
    data,
    loading,
    searchValue,
    handleSearch,
    resetSearch,
    loadMore,
    handleScroll,
  };
};

export default useDropdownData;
